import { ActivityLogTab } from "@features/ax/shared/ActivityLog";
import { NotesTab } from "@features/ax/shared/NotesTab";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { navigateToMap } from "@shared/utils/utils";
import {
  ArrowLeft,
  Camera,
  HelpCircle,
  History,
  MessageSquare,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useIncidentDetail } from "../../hooks";
import { LinkAssetModal } from "./linkAssetModal";
import { QuickActionsCard } from "./quickActions";
import { IncidentDocumentsTab } from "./tabs/attachmentTab";

const IncidentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLinkAssetModalOpen, setIsLinkAssetModalOpen] = useState(false);
  const { data: incidentData } = useIncidentDetail(id);
  const incidentSource = "4";

  const incidentTabComponents = {
    attachments: {
      label: "Files & Documents",
      icon: <Camera className="h-4 w-4" />,
      component: <IncidentDocumentsTab data={incidentData?.attachments} />,
    },
    notes: {
      label: "Notes",
      icon: <MessageSquare className="h-4 w-4" />,
      component: (
        <NotesTab requestId={id} source={incidentSource} title="Incident" />
      ),
      shortLabel: "Notes",
    },
    activityLog: {
      label: "Activity Log",
      icon: <History className="h-4 w-4" />,
      component: <ActivityLogTab entityId={id} entityType={incidentSource} />,
    },
  };

  console.log(
    "incidentData?.linkedServiceOrders",
    incidentData?.linkedServiceOrders
  );

  const incidentDetails = [
    {
      label: "Incident ID",
      value: incidentData?.incidentNo,
      tooltip: "A unique system-generated identifier.",
    },
    {
      label: "Reported By",
      value: incidentData?.customerName ?? "NA",
      tooltip:
        "Person or system that logged the incident. Can be a customer (via web portal, app, or call center), a technician, or an automated system alert. Used for contact and audit purposes.",
    },
    {
      label: "Customer Contact",
      value: incidentData?.customerContactNo ?? "NA",
      tooltip: "Primary phone number of the reporter.",
    },
    {
      label: "Date Reported",
      value: incidentData?.incidentAriseDate ?? "NA",
      tooltip: "The exact date the incident was recorded in the system. ",
    },
    {
      label: "Incident Type",
      value: incidentData?.incidentTypeDisplay ?? "NA",
      tooltip:
        "the type of issue being reported (e.g., Leak, No Supply, Quality Complaint). The type determines routing rules, SLA targets, and reporting categories.",
    },
    {
      label: "Priority",
      value: incidentData?.priorityDisplay ?? "NA",
      tooltip: "urgency of the incident",
    },
    {
      label: "Status",
      value: incidentData?.statusDisplay ?? "NA",
      tooltip: "Shows the current stage of the incident workflow.",
    },
  ];

  const linkedAssetDetails = [
    {
      label: "Asset ID",
      value: incidentData?.asset?.assetNo,
      tooltip: "The unique identifier for this asset.",
    },
    {
      label: "Asset Name",
      value: incidentData?.asset?.assetName ?? "NA",
      tooltip: "The common name used to identify this asset.",
    },
    {
      label: "Status",
      value: incidentData?.asset?.statusDisplay ?? "NA",
      tooltip: "The current operational status of the asset.",
    },
    {
      label: "Description",
      value: incidentData?.asset?.description ?? "NA",
      tooltip: "Short description for this asset.",
    },
  ];

  return (
    <div className="p-2 space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <div className="space-y-6">
          <Card>
            <div className="flex items-center gap-2">
              <ArrowLeft
                className="w-4 h-4 cursor-pointer text-gray-700"
                onClick={() => navigate(-1)}
              />
              <CardTitle>Incident Details</CardTitle>
            </div>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {incidentDetails.map((detail, idx) => (
                  <div>
                    <ValueLabel
                      label={detail.label}
                      value={detail.value}
                      tooltip={detail.tooltip}
                    />
                  </div>
                ))}
              </div>
              <div>
                <ValueLabel
                  label="Customer's Description"
                  value={incidentData?.description}
                  tooltip="Short description of the reported issue provided during creation. "
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Location & Investigation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex gap-2">
                  <p className="text-xs text-muted-foreground tracking-wide">
                    Reported Location
                  </p>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-gray-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        {"Primary address or site of the reported incident"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </div>

                <p
                  className="text-sm text-blue-600 font-medium cursor-pointer hover:underline"
                  onClick={() => {
                    navigateToMap(
                      incidentData?.latitude,
                      incidentData?.longitude
                    );
                  }}
                >
                  {incidentData?.location || "NA"}
                </p>
              </div>
              <div>
                <ValueLabel
                  label="Assigned To"
                  value={
                    incidentData?.linkedServiceOrders[0]?.assignedName ?? "NA"
                  }
                  tooltip="Name of the field technician to which this incident is assigned."
                />
              </div>
              <div>
                <ValueLabel
                  label="Technician's Notes"
                  value={incidentData?.technicianNotes ?? "NA"}
                  tooltip="field updates and investigation findings."
                />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <QuickActionsCard
            hasLinkedAssets={incidentData?.asset ? true : false}
            isIgnored={incidentData?.statusDisplay === "Ignored"}
            incidentDetails={incidentData}
          />
          <Card>
            <CardHeader>
              <CardTitle>Linked Assets</CardTitle>
            </CardHeader>
            <CardContent>
              {!incidentData?.asset ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-gray-500 mb-4">
                    No assets have been linked to this incident yet.
                  </p>
                  <Button
                    className="flex items-center gap-2"
                    onClick={() => setIsLinkAssetModalOpen(true)}
                  >
                    <Plus className="w-4 h-4" />
                    Link Asset
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 ml-4">
                  {linkedAssetDetails.map((detail, idx) => (
                    <ValueLabel
                      key={idx}
                      label={detail.label}
                      value={detail.value}
                      tooltip={detail.tooltip}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Card>
        <CardContent className="p-2">
          <Tabs
            tabComponents={incidentTabComponents}
            defaultValue="history"
            urlMapping={{
              history: "history",
              attachments: "attachments",
              notes: "notes",
            }}
            tabsListClassName="w-full grid grid-cols-3 bg-muted/30 p-1"
            idPrefix="incident-tab"
            level={0}
          />
        </CardContent>
      </Card>

      <LinkAssetModal
        isOpen={isLinkAssetModalOpen}
        onClose={() => setIsLinkAssetModalOpen(false)}
        incidentId={incidentData?.incidentNo}
        incidentDetails={incidentData}
      />
    </div>
  );
};

export default IncidentDetail;
